import { BooleanInput } from '@angular/cdk/coercion';
import { DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { LangDefinition, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';

interface FlagCodes {
    [key: string]: string;
}

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
        NgTemplateOutlet,
        TranslocoModule,
    ],
    styles: `
        .user-icon-inverse {
            @apply rounded-full bg-gray-500;

            svg {
                fill: rgb(17 24 39); // gray-900
            }
        }
    `,
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    @Input() avatarSize: string = 'small'; // Default size is 'small'
    user: User;

    availableLangs: LangDefinition[];

    private _unsubscribeAll: Subject<void> = new Subject<void>();
    private _document = inject(DOCUMENT);
    private _router = inject(Router);
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _userService = inject(UserService);
    private _translocoService = inject(TranslocoService);
    private _authService = inject(AuthService);

    flagCodes: FlagCodes;

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
            this.user = user;
            // Mark for check
            this._changeDetectorRef.markForCheck();
        });

        this.availableLangs = this._translocoService.getAvailableLangs() as LangDefinition[];

        this.flagCodes = {
            en: 'us',
            es: 'es',
            fr: 'fr',
            pt: 'pt',
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._authService.signOut().subscribe(() => {
            this._router.navigate(['/sign-out']);
        });
    }

    /**
   * Toggle the fullscreen mode
  }

  /**
   * Toggle the fullscreen mode
   */
    toggleFullscreen(): void {
        if (!this._document.fullscreenEnabled) {
            console.log('Fullscreen is not available in this browser.');
            return;
        }

        // Check if the fullscreen is already open
        const fullScreen = this._document.fullscreenElement;

        // Toggle the fullscreen
        if (fullScreen) {
            this._document.exitFullscreen();
        } else {
            this._document.documentElement.requestFullscreen().catch(() => {
                console.error('Entering fullscreen mode failed.');
            });
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: LangDefinition): string {
        return item.id;
    }

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        //coming soon languges
        if (lang === 'pt' || lang === 'fr' || lang === 'es') {
            return;
        }

        // Set the active lang
        this._translocoService.setActiveLang(lang);
    }

    navigateToSettings(): void {
        this._router.navigate(['/settings/profile']);
    }
}
